import { useCallback, useEffect, useMemo, useState } from 'react'
import { cexJson, formatCexInlineDataError } from '../lib/cexApi.js'
import { useCexWebSocket } from './useCexWebSocket.js'

export function useAccountPnl(accountId, symbol) {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const subject = accountId ? `cex.pnl.${accountId}.updated` : null
  const subjects = useMemo(() => (subject ? [subject] : []), [subject])
  const { events, connected } = useCexWebSocket({ subjects, trackEvents: Boolean(subject) })

  const load = useCallback(async ({ sync = false } = {}) => {
    if (!accountId) return null
    setBusy(true)
    setError('')
    try {
      const path =
        sync && symbol
          ? `/api/cex/accounts/${encodeURIComponent(accountId)}/pnl/sync?symbol=${encodeURIComponent(symbol)}`
          : `/api/cex/accounts/${encodeURIComponent(accountId)}/pnl`
      const res = await cexJson(path, { method: sync && symbol ? 'POST' : 'GET' })
      setData(res)
      return res
    } catch (e) {
      setError(formatCexInlineDataError(e) || '')
      return null
    } finally {
      setBusy(false)
    }
  }, [accountId, symbol])

  useEffect(() => {
    if (!accountId) {
      setData(null)
      return
    }
    // Sync (POST) only on mount/symbol change; subsequent polls use GET to
    // avoid hammering the exchange on every 30-second tick.
    void load({ sync: Boolean(symbol) })
    const iv = window.setInterval(() => void load({ sync: false }), 30_000)
    return () => window.clearInterval(iv)
  }, [accountId, symbol, load])

  useEffect(() => {
    const last = events[events.length - 1]
    if (!last || last.subject !== subject || !last.data) return
    setData(last.data)
  }, [events, subject])

  return { data, error, busy, connected, reload: load }
}

